<template>
<div>
  <el-row type="flex" class="title-bar" :style="'justify-content:' + (operationFlag ? 'flex-end;' : 'space-between;')">
    <w-perf-analysis-time-info v-if="!operationFlag" :ref="timeInfoRef" :checkDatetime="checkDatetime" @change="onTimeTypeChange" @reload="onCurrentDataReLoad"/>
    <w-header-search-button @click="onShowSearchButtonClick"></w-header-search-button>
  </el-row>
  <el-table-wrapper v-loading="isLoading" :data="tableData" :columns="sessionColumns" :pagination="true" :column-default="columnDefault" :show-custom-header="true" :show-search-tool="showSearchTool">
    <template slot-scope="scope" slot="sql-slot">
      <i class="far fa-search-plus c-pointer" @click="showSqlDetailPopup(scope.row)"></i>
    </template>
  </el-table-wrapper>
  <w-dbsession-sql-query-show-popup ref="sqlDetailPopup" />
</div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import { convertValuByUnit } from '@/utils/number';
import { analysisTimeType } from '@/constants/performance';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import msgUtil from '@/utils/message';
import { columnDefault } from '@/constants/table';
import databaseTableInfoAPI from '@/api/performance/database/database-table-info';
import AnalysisTableMixin from '@/mixins/performance/analysis-table-ex';
import WPerfAnalysisTimeInfo from '@/views/performance/node/analysis/components/PerfAnalysisTimeInfo';
import WDbsessionSqlQueryShowPopup from '../../../popup/DbsessionSqlQueryShowPopup';

export default {
  name: 'WDbSessionTiberoTable',
  mixins: [AnalysisTableMixin],
  components: {
    WDbsessionSqlQueryShowPopup,
    WPerfAnalysisTimeInfo,
  },
  data() {
    return {
      isLoading: false,
      checkDatetime: null,
      columnDefault,
      tableData: [],
      showSearchTool: false,
      columns: [
        { prop: 'SID', label: '세션ID', sortable: true, searchable: true, width: 80, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'USERNAME', label: '사용자', sortable: true, searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'TERMINAL', label: '터미널', sortable: true, searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'PROGRAM', label: '프로그램명', sortable: true, searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'PROCESS', label: 'Process', sortable: true, searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'STATUS', label: '세션상태', sortable: true, searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'REQ_SERVICE_TIME', label: '서비스 요청 시간누적(초)', sortable: true, formatter: this.convertValue },
        { prop: 'MEM_USE', label: '메모리 사용량(Bytes)', sortable: true, formatter: this.convertValue },
        { prop: 'BLOCK_GETS', label: 'Block gets', sortable: true, formatter: this.convertValue },
        { prop: 'CONSISTENT_GETS', label: 'Consistent get', sortable: true, formatter: this.convertValue },
        { prop: 'BLOCK_CHANGES', label: 'Block Changes', sortable: true, formatter: this.convertValue },
        { prop: 'PHYSICAL_READS', label: 'Physical Reads', sortable: true, formatter: this.convertValue },
        { prop: 'LOGON_TIME', label: '접속 시작시간', sortable: true, searchable: true, searchType: 'date', formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'SERIAL', label: '일련번호', sortable: true, searchable: true, formatter: this.getTableValueAfterCheckEmpty },
        { prop: 'SQL_ID', label: 'SQL문ID', sortable: true, formatter: this.getTableValueAfterCheckEmpty },
      ],
    };
  },
  computed: {
    sessionColumns() {
      if (this.timeType === analysisTimeType.HIST.value) {
        return this.columns;
      }
      const columns = cloneDeep(this.columns);
      columns.push({ label: 'SQL문', align: 'center', scopedSlot: 'sql-slot', width: 60 });
      return columns;
    },
  },
  methods: {
    /**
     * 데이터를 로드한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadData() {
      const params = {
        objId: this.selectedNode.obj.objId,
        baselineDatetime: this.operationFlag ? (this.getDatetimeParams(new Date().getTime(), true)).startDatetime : this.plotLineDatetime,
        managetypeId: 'DBMS',
      };
      if (this.timeType === analysisTimeType.CURRENT.value || this.operationFlag) {
        params.queryId = 'DBTBSessionInfo';
        params.parameterMap = {
          orderby: 'LOGON_TIME',
        };
        this.isLoading = true;
        databaseTableInfoAPI.getCurrentDbsessionList(params).then((result) => {
          if (result.data.success === true) {
            this.tableData = result.data.data;
          } else {
            this.failToLoadData();
          }
        }).catch(() => {
          this.failToLoadData();
        }).finally(() => {
          this.isLoading = false;
        });
      } else {
        this.isLoading = true;
        databaseTableInfoAPI.getDbsessionTiberoList(params).then((result) => {
          if (result.data.success === true) {
            const snapshotData = result.data.data;
            this.tableData = snapshotData.snapshotDoc;
            this.checkDatetime = snapshotData.timestamp;
          } else {
            this.failToLoadData();
          }
        }).catch(() => {
          this.failToLoadData();
        }).finally(() => {
          this.isLoading = false;
        });
      }
    },
    /**
     * 데이터 로드 실패 처리
     * @author Ju
     * @return {[type]} [description]
     */
    failToLoadData() {
      this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, 'DB 세션', false));
    },
    /**
     * [테이블 컬럼 포멧]
     * @author {Yang}
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    convertValue(row, column, value) {
      return isNil(value) ? EMPTY_TEXT : convertValuByUnit(value, null, true);
    },
    /**
     * SQL문 상세 팝업을 연다.
     * @author Ju
     * @param  {[type]} row 세션 정보
     */
    showSqlDetailPopup(row) {
      this.$refs.sqlDetailPopup.open(row);
    },
    /**
     * 테이블 데이터 빈값 확인
     * @author Yang
     * @param  {Object} row
     * @param  {Object} column
     * @param  {String/Number} valu
     */
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    onShowSearchButtonClick() {
      this.showSearchTool = !this.showSearchTool;
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/database/components/table/DbSession/DbSessionTiberoTable.vue